/** @jsxImportSource react */

import React, { useMemo } from "react";
import type { MesaWithComparison } from "../lib/types";
import {
  getDistritoName,
  resolveDistritoId,
  DISTRITO_ENTRIES,
} from "../../shared/lib/district-mappings";

interface ActasPorProvinciaChartProps {
  data: MesaWithComparison[];
}

export function ActasPorProvinciaChart({ data }: ActasPorProvinciaChartProps) {
  // Agrupar actas por provincia - SIEMPRE mostrar todas las provincias
  const chartData = useMemo(() => {
    // Contar actas por provincia
    const grouped = new Map<string, number>();
    data.forEach((mesa) => {
      const provId = resolveDistritoId(mesa.data.distrito);
      if (provId) {
        grouped.set(provId, (grouped.get(provId) || 0) + 1);
      }
    });

    // Crear array con TODAS las provincias, incluso con count=0
    const result = DISTRITO_ENTRIES.map(([provId]) => ({
      provId,
      provName: getDistritoName(provId) || provId,
      count: grouped.get(provId) || 0,
    })).sort((a, b) => a.provName.localeCompare(b.provName));

    return result;
  }, [data]);

  // Calcular el máximo para la escala
  const maxCount = useMemo(() => {
    const max = Math.max(...chartData.map((d) => d.count), 1);
    return Math.ceil(max * 1.1); // +10%
  }, [chartData]);

  return (
    <div className="flex-1 w-full h-full min-h-0 min-w-0 flex flex-col justify-start px-4 py-2 gap-1">
      {chartData.map((prov) => {
        const barWidth = (prov.count / maxCount) * 100;
        return (
          <div
            key={prov.provId}
            className="flex items-center gap-3 flex-1 min-h-0"
          >
            {/* Etiqueta provincia */}
            <div
              className="text-xs text-slate-700 font-semibold text-right w-32 shrink-0"
              title={prov.provName}
            >
              {prov.provName}
            </div>
            {/* Barra */}
            <div className="relative flex items-center h-full flex-1">
              {/* Eje 0 */}
              <div className="absolute left-0 top-0 w-0.5 h-full bg-slate-300 z-0"></div>
              {/* Barra */}
              <div
                className="absolute top-0 left-0 h-full rounded bg-slate-500 opacity-80"
                style={{
                  width: `${barWidth}%`,
                }}
                title={`${prov.provName}: ${prov.count} actas`}
              ></div>
              {/* Valor */}
              <span
                className="absolute text-[10px] text-slate-700 font-bold z-10 ml-1"
                style={{
                  left: `${barWidth}%`,
                }}
              >
                {prov.count}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
